import { googleConsentTypes, type ConsentAction, type ConsentCategory, type ConsentPreferences, type ConsentState, type GoogleConsentType, type PublicConfiguration } from './types'
import { createUuid } from './uuid'

export const categoryConsentTypes: Record<ConsentCategory, GoogleConsentType[]> = {
  ads: ['ad_storage', 'ad_user_data', 'ad_personalization'],
  analytics: ['analytics_storage'],
  functionality: ['functionality_storage'],
  personalization: ['personalization_storage'],
  security: ['security_storage'],
}
const consentCategoryOrder: ConsentCategory[] = ['security', 'personalization', 'functionality', 'analytics', 'ads']

export const emptyPreferences = (): ConsentPreferences => ({
  ads: false,
  analytics: false,
  functionality: false,
  personalization: false,
})

export function configuredCategories(configuration: PublicConfiguration) {
  const enabled = new Set(configuration.project.policy.googleConsentTypes)
  return consentCategoryOrder
    .filter((category) => categoryConsentTypes[category].some((type) => enabled.has(type)))
}

export function googleState(configuration: PublicConfiguration, preferences: ConsentPreferences) {
  const configured = new Set(configuration.project.policy.googleConsentTypes)
  const state = Object.fromEntries(googleConsentTypes.map((type) => [type, 'denied'])) as ConsentState['googleConsent']
  for (const [category, types] of Object.entries(categoryConsentTypes) as Array<[ConsentCategory, GoogleConsentType[]]>) {
    const granted = category === 'security' || preferences[category]
    for (const type of types) if (configured.has(type) && granted) state[type] = 'granted'
  }
  return state
}

export function grantedTypes(state: ConsentState['googleConsent']) {
  return googleConsentTypes.filter((type) => state[type] === 'granted')
}

export function setGoogleDefaults() {
  const defaults = Object.fromEntries(googleConsentTypes.map((type) => [type, 'denied']))
  sendGtag('consent', 'default', defaults)
}

export function updateGoogleConsent(state: ConsentState['googleConsent']) {
  sendGtag('consent', 'update', state)
}

function sendGtag(...args: unknown[]) {
  window.dataLayer = window.dataLayer ?? []
  if (!window.gtag) window.gtag = (...values: unknown[]) => { window.dataLayer?.push(values) }
  window.gtag(...args)
}

export function readStoredConsent(projectKey: string, policyVersion: string) {
  try {
    const raw = localStorage.getItem(storageKey(projectKey))
    if (!raw) return null
    const state = JSON.parse(raw) as ConsentState
    if (state.policyVersion !== policyVersion || Date.parse(state.expiresAt) <= Date.now()) {
      localStorage.removeItem(storageKey(projectKey))
      return null
    }
    return state
  } catch {
    return null
  }
}

export function saveStoredConsent(projectKey: string, state: ConsentState) {
  try { localStorage.setItem(storageKey(projectKey), JSON.stringify(state)) } catch { /* session-only fallback */ }
}

export function getVisitorId(projectKey: string) {
  const key = `cmp:visitor:${projectKey}`
  try {
    const existing = localStorage.getItem(key)
    if (existing) return existing
    const value = createUuid()
    localStorage.setItem(key, value)
    return value
  } catch {
    return createUuid()
  }
}

export interface PendingConsent {
  url: string
  body: Record<string, unknown>
}

export function queuePending(projectKey: string, item: PendingConsent) {
  try {
    const queue = readPending(projectKey).filter((entry) => entry.body.eventId !== item.body.eventId)
    localStorage.setItem(pendingKey(projectKey), JSON.stringify([...queue, item]))
  } catch { /* retry is best-effort */ }
}

export function readPending(projectKey: string): PendingConsent[] {
  try { return JSON.parse(localStorage.getItem(pendingKey(projectKey)) ?? '[]') as PendingConsent[] } catch { return [] }
}

export function replacePending(projectKey: string, pending: PendingConsent[]) {
  try {
    if (pending.length) localStorage.setItem(pendingKey(projectKey), JSON.stringify(pending))
    else localStorage.removeItem(pendingKey(projectKey))
  } catch { /* retry is best-effort */ }
}

export async function retryPending(projectKey: string) {
  const failed: PendingConsent[] = []
  for (const item of readPending(projectKey)) {
    try {
      const response = await fetch(item.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item.body),
      })
      if (!response.ok) failed.push(item)
    } catch {
      failed.push(item)
    }
  }
  replacePending(projectKey, failed)
}

export function activateManagedScripts(state: ConsentState, cspNonce?: string) {
  const activate = (root: ParentNode = document) => {
    const selector = 'script[type="text/plain"][data-cmp-category]'
    const scripts = [
      ...(root instanceof HTMLScriptElement && root.matches(selector) ? [root] : []),
      ...root.querySelectorAll<HTMLScriptElement>(selector),
    ]
    scripts.forEach((source) => {
      if (source.dataset.cmpActivated === 'true') return
      const category = source.dataset.cmpCategory as ConsentCategory | undefined
      if (!category || !hasCategoryConsent(state, category)) return

      const script = document.createElement('script')
      for (const attribute of [...source.attributes]) {
        if (attribute.name === 'type' || attribute.name === 'data-src' || attribute.name.startsWith('data-cmp-')) continue
        script.setAttribute(attribute.name, attribute.value)
      }
      script.type = source.dataset.type ?? 'text/javascript'
      const scriptSource = source.dataset.src
      if (scriptSource) script.src = scriptSource
      else script.textContent = source.textContent
      if (cspNonce) script.nonce = cspNonce
      source.dataset.cmpActivated = 'true'
      source.after(script)
    })
  }

  activate()
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) for (const node of [...mutation.addedNodes]) {
      if (node instanceof Element) activate(node)
    }
  })
  observer.observe(document.documentElement, { childList: true, subtree: true })
  return () => observer.disconnect()
}

export function hasCategoryConsent(state: ConsentState | null, category: ConsentCategory) {
  if (!state) return false
  if (category === 'security') return state.googleConsent.security_storage === 'granted'
  return categoryConsentTypes[category].some((type) => state.googleConsent[type] === 'granted')
}

export function actionForPreferences(preferences: ConsentPreferences): ConsentAction {
  return Object.values(preferences).every(Boolean)
    ? 'accept_all'
    : Object.values(preferences).every((value) => !value) ? 'reject_all' : 'save_preferences'
}

function storageKey(projectKey: string) { return `cmp:consent:${projectKey}` }
function pendingKey(projectKey: string) { return `cmp:pending:${projectKey}` }
